/**
 * AdapterProvider — expõe um Adapter via Context.
 *
 * Em v1 usa mock por default. Fase 3 adiciona seleção mock|ws via
 * env/query param. Componentes consomem via useAdapter().
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { Adapter, AdapterMode } from '../types/adapter'
import type { Session } from '../types/session'
import { createMockAdapter } from './mock'
import { createWebSocketAdapter } from './websocket'

const AdapterContext = createContext<Adapter | null>(null)

interface Props {
  session: Session
  children: ReactNode
  /** Força o modo do adapter, ignorando env e query param. Usado pelo
   *  preview mode (que não deve tentar WS porque não há sessão no backend). */
  forceMode?: AdapterMode
}

function resolveMode(paramMode: string | null): AdapterMode {
  // Precedência: ?adapter=... > VITE_ADAPTER_MODE > default (ws).
  // Mock fica disponível via ?adapter=mock para dev sem backend.
  if (paramMode === 'mock' || paramMode === 'ws') return paramMode
  const envMode = (import.meta.env.VITE_ADAPTER_MODE as string | undefined) ?? undefined
  if (envMode === 'mock' || envMode === 'ws') return envMode
  return 'ws'
}

export function AdapterProvider({ session, children, forceMode }: Props) {
  const [search] = useSearchParams()
  const mode = forceMode ?? resolveMode(search.get('adapter'))

  const adapter = useMemo<Adapter>(() => {
    if (mode === 'mock') {
      return createMockAdapter({
        sessionId: session.sessionId,
        userId: session.userId,
        role: session.role,
      })
    }
    // Identidade anônima (fluxo QR) nunca usa o JWT que por acaso exista no
    // localStorage — senão um professor logado testando o join entraria como master.
    const token = session.isAnonymous ? null : localStorage.getItem('auth_token')
    return createWebSocketAdapter({
      sessionId: session.sessionId,
      userId: session.userId,
      role: session.role,
      token: token ?? null,
      displayName: session.displayName,
    })
  }, [mode, session.sessionId, session.userId, session.role, session.displayName, session.isAnonymous])

  useEffect(() => {
    return () => {
      adapter.disconnect()
    }
  }, [adapter])

  return <AdapterContext.Provider value={adapter}>{children}</AdapterContext.Provider>
}

export function useAdapter(): Adapter {
  const adapter = useContext(AdapterContext)
  if (!adapter) {
    throw new Error('useAdapter deve ser usado dentro de <AdapterProvider>')
  }
  return adapter
}
